import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore'
import { auth, db } from '../config/firebase'
import { isPersistedLocalSession } from '../config/devTestAccounts'
import { canonicalizeMaterialVariant } from '../utils/materialHierarchy'
import { quoteSpecificDetails } from '../utils/clothingDesignHelper'
import { uploadDesignImage } from './designStorage'
import { suppressUnloadPrompt } from '../utils/unloadGuard'
import { errorFromApi } from '../utils/customerError'

const LOCAL_QUOTES_KEY = 'styleai_customQuoteRequests'

function readLocalQuotes(uid) {
  if (typeof window === 'undefined' || !uid) return []
  try {
    const raw = window.localStorage.getItem(`${LOCAL_QUOTES_KEY}:${uid}`)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeLocalQuotes(uid, items) {
  window.localStorage.setItem(`${LOCAL_QUOTES_KEY}:${uid}`, JSON.stringify(items))
}

export const CUSTOM_QUOTE_COLLECTION = 'customQuoteRequests'

export const CUSTOM_QUOTE_STATUSES = ['pending_review', 'rejected', 'submitted', 'quoted', 'paid']

export const CUSTOM_QUOTE_SIZES = [
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  'XXXL',
  'Custom measurements (see notes)'
]

const CLIENT_FORBIDDEN_KEYS = [
  'price',
  'quotePrice',
  'quotedPrice',
  'quotedAmount',
  'amount',
  'amountCents',
  'manufacturerCost',
  'margin',
  'quotedAt',
  'quotedBy',
  'paidAt',
  'paid',
  'paymentId',
  'stripePaymentIntentId',
  'stripeSessionId',
  'stripeCheckoutSessionId',
  'stripeCustomCheckoutSessionId',
  'stripeCustomerId',
  'shippingAddress',
  'shipping',
  'fulfillment',
  'fulfillmentReleasedAt',
  'fulfillmentNotifiedAt',
  'amountPaid',
  'productionStatus'
]

export const SHIP_ZIP_ERROR = 'Enter a 5-digit US ZIP code.'

export function isValidShipZip(value) {
  return /^\d{5}$/.test(String(value || '').trim())
}

function toMillis(value) {
  if (!value) return 0
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (typeof value.seconds === 'number') return value.seconds * 1000
  if (value instanceof Date) return value.getTime()
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function stripForbiddenKeys(payload) {
  const next = { ...payload }
  CLIENT_FORBIDDEN_KEYS.forEach((key) => {
    delete next[key]
  })
  return next
}

function collectImageSources(generatedDesign) {
  if (!generatedDesign) return []
  const sources = [
    generatedDesign.mainImage,
    generatedDesign.image,
    generatedDesign.backImage
  ].filter((src) => typeof src === 'string' && src.trim())
  return [...new Set(sources)]
}

function toUploadableUrl(src) {
  if (src.startsWith('data:') || src.startsWith('http://') || src.startsWith('https://')) {
    return src
  }
  if (src.startsWith('/') && typeof window !== 'undefined') {
    return `${window.location.origin}${src}`
  }
  return src
}

async function uploadQuoteImages(generatedDesign, uid) {
  const sources = collectImageSources(generatedDesign)
  const urls = []
  for (const src of sources) {
    if (src.startsWith('https://firebasestorage.googleapis.com') || src.includes('firebasestorage.app')) {
      urls.push(src)
      continue
    }
    urls.push(await uploadDesignImage(toUploadableUrl(src), uid))
  }
  return urls
}

/**
 * Persist a Custom-tier quote request. Client may only create status 'submitted'
 * with no price fields. Caller must be a signed-in Firebase user.
 */
export async function submitCustomQuoteRequest({
  user,
  designData,
  customInstructions,
  generatedDesign,
  size,
  shipZip,
  notes
}) {
  if (!user?.uid) {
    throw new Error('Sign in to request a custom quote')
  }
  if (!size) {
    throw new Error('Select a size')
  }
  const zip = typeof shipZip === 'string' ? shipZip.trim() : ''
  if (!isValidShipZip(zip)) {
    throw new Error(SHIP_ZIP_ERROR)
  }
  if (!generatedDesign) {
    throw new Error('Generate a design before requesting a quote')
  }

  const localSession = isPersistedLocalSession(user)
  let imageUrls = collectImageSources(generatedDesign).map(toUploadableUrl)
  if (!localSession) {
    imageUrls = await uploadQuoteImages(generatedDesign, user.uid)
  }
  const imageUrl = imageUrls[0] || generatedDesign.mainImage || generatedDesign.image || ''
  if (!imageUrl) {
    throw new Error('A design image is required to request a quote')
  }

  const payload = stripForbiddenKeys({
    size,
    shipZip: zip,
    notes: typeof notes === 'string' ? notes.trim() : '',
    design: {
      clothingType: designData?.type || generatedDesign.type || '',
      material: designData?.material || generatedDesign.material || '',
      materialType: canonicalizeMaterialVariant(designData?.materialVariant) || '',
      style: designData?.style || generatedDesign.style || '',
      color: designData?.color || generatedDesign.color || '',
      specificDetails: quoteSpecificDetails(
        designData?.material || generatedDesign.material || '',
        customInstructions || ''
      ),
      fit: designData?.fit || '',
      neckline: designData?.neckline || '',
      sleeveLength: designData?.sleeveLength || '',
      hem: designData?.hem || '',
      presentationMode: generatedDesign.presentationMode || '',
      modelGender: generatedDesign.modelGender || '',
      ...(generatedDesign.designPlacement ? { designPlacement: generatedDesign.designPlacement } : {}),
      ...(generatedDesign.frontDescription ? { frontDescription: generatedDesign.frontDescription } : {}),
      ...(generatedDesign.backDesign === true
        ? { backDesign: true, backDescription: generatedDesign.backDescription || '' }
        : {})
    },
    imageUrl,
    imageUrls: imageUrls.length ? imageUrls : [imageUrl],
    generationId: generatedDesign.generationId || '',
    savedDesignId: generatedDesign.id || '',
    designTitle: generatedDesign.title || `Custom ${designData?.type || 'Design'}`
  })

  if (localSession) {
    const saved = {
      ...payload,
      userId: user.uid,
      userEmail: user.email || null,
      userName: user.name || user.displayName || null,
      status: 'pending_review',
      statusLabel: 'Pending review',
      id: `local-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      localOnly: true
    }
    writeLocalQuotes(user.uid, [saved, ...readLocalQuotes(user.uid)])
    return saved
  }

  if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
    throw new Error('Sign in to request a custom quote')
  }

  const { getAuthHeaders, API_BASE_URL } = await import('./usageService')
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE_URL}/custom-quotes/submit`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw errorFromApi(data, res.status, "We couldn't send your request. Please try again.")
  }
  return data.request || { id: data.id, ...payload }
}

// Admin-only fields. The server keeps them in internal/review; older docs may still carry them.
const ADMIN_ONLY_QUOTE_KEYS = [
  'manufacturerCost',
  'margin',
  'quotedBy',
  'reviewStatus',
  'manufacturerApprovedAt',
  'rejectedAt',
  'manufacturerNotifiedAt',
  'manufacturerNotifyStatus',
  'manufacturerNotifyError',
  'fulfillmentNotifiedAt',
  'fulfillmentNotifyStatus',
  'fulfillmentNotifyError',
  'fulfillmentReleasedAt',
  'legacyNotice',
  'notifications'
]

function redactCustomerQuote(doc) {
  const safe = { ...doc }
  ADMIN_ONLY_QUOTE_KEYS.forEach((key) => {
    delete safe[key]
  })
  return safe
}

export async function startCustomQuoteCheckout(requestId) {
  if (!requestId || !auth.currentUser) {
    throw new Error('Sign in to pay this quote')
  }
  const { getAuthHeaders, API_BASE_URL } = await import('./usageService')
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE_URL}/custom-checkout`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      requestId,
      returnOrigin: typeof window !== 'undefined' ? window.location.origin : undefined
    })
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw errorFromApi(data, res.status, "We couldn't start checkout. Please try again.")
  }
  if (!data.checkoutUrl) throw new Error("We couldn't start checkout. Please try again.")
  suppressUnloadPrompt()
  window.location.assign(data.checkoutUrl)
  return data
}

export async function syncCustomQuoteCheckout(requestId, sessionId) {
  if (!requestId || !sessionId || !auth.currentUser) return null
  const { getAuthHeaders, API_BASE_URL } = await import('./usageService')
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE_URL}/custom-checkout/sync`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ requestId, sessionId })
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw errorFromApi(data, res.status, "We couldn't confirm your payment yet. Please refresh in a moment.")
  }
  return data
}

export async function notifyManufacturerQuoteRequest(requestId) {
  if (!requestId || !auth.currentUser) return null
  const { getAuthHeaders } = await import('./usageService')
  const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.DEV ? '/api' : 'http://localhost:3003/api')
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE_URL}/custom-quotes/${requestId}/notify-manufacturer`, {
    method: 'POST',
    headers
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw errorFromApi(data, res.status, "We couldn't send your request. Please try again.")
  }
  return data
}

export async function fetchMyCustomQuoteRequests(uid, user) {
  if (!uid) return []
  if (isPersistedLocalSession(user) || !auth.currentUser) {
    return readLocalQuotes(uid).sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt))
  }
  const snap = await getDocs(
    query(collection(db, CUSTOM_QUOTE_COLLECTION), where('userId', '==', uid))
  )
  return snap.docs
    .map((d) => redactCustomerQuote({ id: d.id, ...d.data() }))
    .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt))
}
